import React, { useState, useEffect, useContext } from 'react';
import {
  Typography, Card, Row, Col, Tag, Statistic, Spin, Table, Tooltip,
  Button, Popconfirm, message, Space, Alert,
} from 'antd';
import { CheckCircleOutlined, WarningOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';
import { useBackFallback, usePageLabel } from '../components/BackChip';

const STATUS_COLOR = {
  assigned:          'blue',
  in_progress:       'orange',
  completed:         'green',
  rescore_requested: 'purple',
};
const STATUS_LABEL = {
  assigned:          'Assigned',
  in_progress:       'In Progress',
  completed:         'Completed',
  rescore_requested: 'Rescore Requested',
};

function scoreColor(pct) {
  if (pct == null) return '#8c8c8c';
  if (pct >= 80) return '#52c41a';
  if (pct >= 60) return '#faad14';
  return '#ff4d4f';
}

function ScoreCell({ pts, maxPts, completed }) {
  if (!completed) return <Typography.Text type="secondary">Pending</Typography.Text>;
  if (pts == null) return <Typography.Text type="secondary">—</Typography.Text>;
  const pct = maxPts > 0 ? Math.round((pts / maxPts) * 100) : null;
  return (
    <Tooltip title={`${pts} / ${maxPts} pts`}>
      <span style={{ color: scoreColor(pct), fontWeight: 600 }}>
        {pts} / {maxPts}
      </span>
    </Tooltip>
  );
}

export default function CalibrationSessionPage() {
  const { sessionId } = useParams();
  const navigate      = useNavigate();
  const currentUser   = useContext(UserContext);
  const isAdmin       = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin';

  const [data, setData]       = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  usePageLabel(data?.session?.Title ? `Calibration: ${data.session.Title}` : null);
  // The Back chip (Layout): where you came from, else the sessions list.
  useBackFallback('/calibration', 'Calibration Sessions');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/calibration/sessions/${sessionId}`)
      .then((r) => setData(r.data))
      .catch(() => message.error('Failed to load calibration session.'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const handleRenameTitle = async (newTitle) => {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === data.session.Title) return;
    setSaving(true);
    try {
      await api.patch(`/api/calibration/sessions/${sessionId}`, { title: trimmed });
      setData((prev) => ({ ...prev, session: { ...prev.session, Title: trimmed } }));
      message.success('Title updated.');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update title.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/calibration/sessions/${sessionId}`);
      message.success('Session deleted.');
      navigate('/calibration');
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to delete session.');
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 64 }}><Spin size="large" /></div>;
  }

  if (!data) return null;

  const { session, cases, questions } = data;

  // Build dynamic evaluator columns for the question comparison table
  const evalColumns = cases.map((c) => ({
    title: (
      <div>
        <div style={{ fontWeight: 600 }}>{c.EvaluatorName}</div>
        <Tag color={STATUS_COLOR[c.Status] ?? 'default'} style={{ marginTop: 2, fontSize: 11 }}>
          {STATUS_LABEL[c.Status] ?? c.Status}
        </Tag>
      </div>
    ),
    key:   `eval_${c.ID}`,
    width: 140,
    align: 'center',
    render: (_, row) => {
      const resp = row.EvaluatorResponses.find((r) => r.CaseID === c.ID);
      if (!resp) return <Typography.Text type="secondary">—</Typography.Text>;
      return (
        <ScoreCell
          pts={resp.PointsEarned}
          maxPts={row.MaxPoints}
          completed={c.Status === 'completed'}
        />
      );
    },
  }));

  const questionColumns = [
    {
      title: 'Section',
      dataIndex: 'SectionName',
      key: 'SectionName',
      width: 130,
      ellipsis: true,
    },
    {
      title: 'Question',
      dataIndex: 'QuestionText',
      key: 'QuestionText',
      ellipsis: true,
    },
    {
      title: 'Max Pts',
      dataIndex: 'MaxPoints',
      key: 'MaxPoints',
      width: 80,
      align: 'right',
    },
    ...evalColumns,
    {
      title: 'Agreement',
      key: 'Agreed',
      width: 110,
      align: 'center',
      render: (_, row) => {
        // Only assess agreement if at least 2 evaluators have responded
        const scored = row.EvaluatorResponses.filter((r) => r.PointsEarned != null);
        if (scored.length < 2) {
          return <Typography.Text type="secondary" style={{ fontSize: 12 }}>Pending</Typography.Text>;
        }
        return row.Agreed
          ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
          : <WarningOutlined style={{ color: '#faad14', fontSize: 16 }} />;
      },
    },
  ];

  const disagreements = questions.filter((q) => {
    const scored = q.EvaluatorResponses.filter((r) => r.PointsEarned != null);
    return scored.length >= 2 && !q.Agreed;
  }).length;

  const allComplete = cases.length > 0 && cases.every((c) => c.Status === 'completed');

  return (
    <>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <Space direction="vertical" size={4}>
          <Typography.Title
            level={4}
            style={{ margin: 0 }}
            editable={isAdmin ? { onChange: handleRenameTitle, tooltip: 'Edit title' } : false}
          >
            {session.Title}
          </Typography.Title>
          <Space size={16} wrap>
            <Typography.Text type="secondary">
              Form: <strong>{session.FormName} v{session.FormVersion}</strong>
            </Typography.Text>
            {session.ContactID && (
              <Typography.Text type="secondary">
                Contact:{' '}
                <Link to={`/calls/${session.ContactID}`}>
                  <strong>{session.ContactID}</strong>
                </Link>
              </Typography.Text>
            )}
            <Typography.Text type="secondary">
              Created by {session.CreatedByName} · {formatTS(session.CreatedAt)}
            </Typography.Text>
          </Space>
        </Space>

        {isAdmin && (
          <Popconfirm
            title="Delete this calibration session?"
            description="All evaluations in this session will also be deleted."
            onConfirm={handleDelete}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete Session</Button>
          </Popconfirm>
        )}
      </div>

      {/* Status summary */}
      {!allComplete && (
        <Alert
          type="info"
          showIcon
          message={`${cases.filter((c) => c.Status === 'completed').length} of ${cases.length} evaluators have completed their assessment.`}
          style={{ marginBottom: 16 }}
        />
      )}
      {allComplete && disagreements > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`All evaluators have completed. ${disagreements} question${disagreements > 1 ? 's have' : ' has'} scoring disagreement${disagreements > 1 ? 's' : ''} — highlighted below.`}
          style={{ marginBottom: 16 }}
        />
      )}
      {allComplete && disagreements === 0 && (
        <Alert
          type="success"
          showIcon
          message="All evaluators have completed and scores are in full agreement."
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Evaluator score cards */}
      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {cases.map((c) => (
          <Col key={c.ID} xs={12} sm={8} md={6}>
            <Card size="small" style={{ textAlign: 'center' }}>
              <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>
                {c.EvaluatorName}
              </Typography.Text>
              <Tag color={STATUS_COLOR[c.Status] ?? 'default'} style={{ marginBottom: 8 }}>
                {STATUS_LABEL[c.Status] ?? c.Status}
              </Tag>
              {c.Score != null ? (
                <div>
                  <Typography.Text strong style={{ fontSize: 20, color: scoreColor(c.Score) }}>
                    {c.Score}%
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    {c.TotalEarned} / {c.TotalMax} pts
                  </Typography.Text>
                </div>
              ) : (
                <Typography.Text type="secondary">—</Typography.Text>
              )}
              <div style={{ marginTop: 6 }}>
                <Link to={`/cases/${c.ID}`} onClick={(e) => e.stopPropagation()}>
                  <Button size="small" type="link" style={{ padding: 0 }}>Open Eval</Button>
                </Link>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Question comparison table */}
      <Typography.Title level={5} style={{ marginBottom: 8 }}>Question-by-Question Comparison</Typography.Title>
      <Table
        rowKey="QuestionID"
        dataSource={questions}
        columns={questionColumns}
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowClassName={(row) => {
          const scored = row.EvaluatorResponses.filter((r) => r.PointsEarned != null);
          return scored.length >= 2 && !row.Agreed ? 'calibration-disagreement-row' : '';
        }}
        locale={{ emptyText: 'No questions found for this form.' }}
      />

      <style>{`
        .calibration-disagreement-row td { background: #fffbe6 !important; }
        .calibration-disagreement-row:hover td { background: #fff1b8 !important; }
      `}</style>
    </>
  );
}
